import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { fileReviewAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import PageHeader from '../components/PageHeader';
import './FileReviewList.css';

const REVIEW_TYPES = ['Initial', 'Annual1', 'Annual2'];
const STATUSES = ['Open', 'Sent_To_SC', 'Ready_For_LSC', 'Review_Complete'];

function statusLabel(status) {
  if (status === 'Review_Complete') return 'Complete';
  return status?.replace(/_/g, ' ') || '—';
}

function FileReviewList({ clientIdOverride = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdFromUrl = searchParams.get('clientId')?.trim() || null;
  const effectiveClientId = clientIdOverride || clientIdFromUrl;
  const isClientContext = !!effectiveClientId;
  const { tenantPath } = useTenantFromUrl();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [clientSearch, setClientSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('not_complete');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    if (!hasPermission('file_reviews.view')) {
      navigate(tenantPath('/dashboard'), { replace: true });
      return;
    }
    loadReviews();
  }, [navigate, tenantPath, effectiveClientId, clientSearch, statusFilter, typeFilter]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (effectiveClientId) params.client_id = effectiveClientId;
      if (clientSearch.trim()) params.q = clientSearch.trim();
      // 'not_complete' = don't send status (get all), then filter out Complete client-side
      if (statusFilter && statusFilter !== 'not_complete') params.status = statusFilter;
      if (typeFilter) params.review_type = typeFilter;
      const res = await fileReviewAPI.listReviews(params);
      let data = res.data?.data ?? [];
      if (statusFilter === 'not_complete') {
        data = data.filter((r) => r.status !== 'Review_Complete');
      }
      setReviews(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const clientName = (r) => [r.client_last_name, r.client_first_name].filter(Boolean).join(', ') || '—';

  return (
    <div className="file-review-list-page">
      <div className="file-review-list-header">
        <PageHeader title={isClientContext ? 'File Reviews' : 'Client File Reviews'}>
          {hasPermission('file_reviews.create') && (
            <Link to={tenantPath('/lists/file-reviews/new')} className="btn-new">
              + New file review
            </Link>
          )}
        </PageHeader>
      </div>

      {error && <p className="file-review-list-error">{error}</p>}

      <div className="file-review-list-info">
        {loading && reviews.length === 0
          ? 'Loading...'
          : reviews.length === 0
            ? (statusFilter === 'not_complete'
                ? (isClientContext ? 'No incomplete file reviews for this client.' : 'No incomplete file reviews found.')
                : (isClientContext ? 'No file reviews for this client.' : 'No file reviews found'))
            : reviews.length === 1
              ? '1 file review'
              : `${reviews.length} file reviews`}
      </div>

      <div className="file-review-list-table-wrap">
        <table className="file-review-list-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Type</th>
              <th>Status</th>
            </tr>
            <tr className="file-review-list-filter-row">
              <th>
                {!isClientContext ? (
                  <input
                    type="text"
                    className="file-review-list-filter-input"
                    placeholder="Search by client..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    aria-label="Filter by client name"
                  />
                ) : null}
              </th>
              <th>
                <select
                  className="file-review-list-filter-select"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by type"
                >
                  <option value="">All</option>
                  {REVIEW_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </th>
              <th>
                <select
                  className="file-review-list-filter-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="not_complete">Not complete</option>
                  <option value="">All</option>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{statusLabel(s)}</option>
                  ))}
                </select>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && reviews.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">Loading...</td>
              </tr>
            ) : reviews.length === 0 ? (
              <tr>
                <td colSpan="3" className="no-data">
                  {statusFilter === 'not_complete'
                    ? (isClientContext ? 'No incomplete file reviews for this client.' : 'No incomplete file reviews found.')
                    : (isClientContext ? 'No file reviews for this client.' : 'No file reviews found')}
                </td>
              </tr>
            ) : (
              reviews.map((r) => (
                <tr
                  key={r.id}
                  className="clickable-row"
                  onClick={() => navigate(tenantPath(`/lists/file-reviews/${r.id}`))}
                >
                  <td>{clientName(r)}</td>
                  <td>{r.review_type || '—'}</td>
                  <td>{statusLabel(r.status)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FileReviewList;
